import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import type { RespondedType, ResponseError } from '@/models/response';
import { getUserId } from '@/stores/auth';
import { FILE_ENDPOINTS_STORAGE_KEY } from '@/utils/constants';
import { fetcher } from '@/utils/fetcher';
import { createLogger } from '@/utils/logger';

const notifyFileEndDomainResultSchema = z.union([
  z.literal('health'),
  z.literal('fetch'),
  z.literal('display'),
  z.literal(true),
]);

const notifyFileEndDomainStoreSchema = z.record(z.string(), notifyFileEndDomainResultSchema);
type NotifyFileEndDomainResult = z.infer<typeof notifyFileEndDomainResultSchema>;

interface NotifyFileEndDomainChangeParams {
  domain: string;
  result: NotifyFileEndDomainResult;
}

export async function notifyFileEndDomainChange({
  domain,
  result,
}: NotifyFileEndDomainChangeParams): Promise<RespondedType> {
  if (!getUserId()) return Promise.resolve({ Code: -1, Msg: 'NOT_AUTHENTICATED' });

  const stored = sessionStorage.getItem(FILE_ENDPOINTS_STORAGE_KEY) || '{}';
  const { data, success } = await notifyFileEndDomainStoreSchema.safeParseAsync(JSON.parse(stored));

  if (success) {
    data[domain] = result;

    sessionStorage.setItem(FILE_ENDPOINTS_STORAGE_KEY, JSON.stringify(data));
  }

  dynamicEndpointLogger.info('NotifyFileEndPointDomainChange', result, domain);

  switch (result) {
    case 'fetch':
    case 'health':
    case 'display':
      return Promise.resolve({ Code: 0, Msg: 'OK' });
    default:
      return fetcher<RespondedType>('/NotifyFileEndPointDomainChange', {
        Domain: domain,
      });
  }
}

const dynamicEndpointLogger = createLogger('Dynamic Endpoint');

export default function useNotifyFileEndDomainChange() {
  return useMutation<RespondedType, ResponseError, NotifyFileEndDomainChangeParams>({
    mutationKey: ['/NotifyFileEndPointDomainChange'],
    mutationFn: (params) => notifyFileEndDomainChange(params),
    onError: undefined,
  });
}
